import { useState } from 'react';
import { usePrintStatus, sendToPrinter, refreshPrintStatus } from '../../hooks/usePrint.js';

/**
 * 🖨 Print — sends a Blake document straight to the printer set for its
 * type in Settings → Printers (via the helper on the shipping PC).
 * Renders nothing until a printer has been set for `docType`, so pages
 * keep their existing Preview buttons alongside it.
 *
 * <PrintButton docType="batch_label" path={`/stock/batches/${id}/label.pdf`} title="Batch label B-123" />
 */
export default function PrintButton({ docType, path, method, body, title, label = 'Print', style, size = 'sm', getBody, onDone }) {
  const { isConfigured, canPrint, printerFor } = usePrintStatus();
  const [state, setState] = useState('idle'); // idle | busy | done | error
  const [msg, setMsg] = useState('');

  if (!isConfigured(docType)) return null;

  const online = canPrint(docType);
  const printer = printerFor(docType);

  async function handleClick(e) {
    e.stopPropagation();
    setState('busy'); setMsg('');
    try {
      const r = await sendToPrinter({ docType, path, method, body: getBody ? getBody() : body, title });
      setState('done'); setMsg(`Printed on ${r.printer}`);
      if (onDone) onDone(r);
      setTimeout(() => setState('idle'), 4000);
    } catch (err) {
      setState('error'); setMsg(err.message);
      refreshPrintStatus();
    }
  }

  const pad = size === 'sm' ? '6px 12px' : '9px 18px';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={state === 'busy' || !online}
        title={online ? `Print on ${printer}` : `The shipping PC isn't connected — use Preview to print from here`}
        style={{
          padding: pad, fontSize: size === 'sm' ? 13 : 14, fontWeight: 600, borderRadius: 8, cursor: online ? 'pointer' : 'not-allowed',
          background: 'var(--pld-green)', color: '#fff', border: 'none', opacity: online ? 1 : 0.5, whiteSpace: 'nowrap',
          ...style,
        }}
      >
        {state === 'busy' ? 'Printing…' : `🖨 ${label}`}
      </button>
      {state === 'done' && <span style={{ fontSize: 12, color: 'var(--pld-green)', fontWeight: 600 }}>✓ {msg}</span>}
      {state === 'error' && <span style={{ fontSize: 12, color: 'var(--status-critical, #dc2626)' }}>{msg}</span>}
    </span>
  );
}
